import Modal from "@components/atoms/Modal";
import useScreenOptionsHooks from "../../../../hooks/useScreenOptionsHooks";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import styled from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { Button, Spacer, VDBText } from "@components/atoms";
import UserSelector from "./components/UserSelector";
import { useGlobalData } from "@redux/slices/globalSlice";
import {
  computePurchaseSeatsPrice,
  formatDate,
  formatToday,
  Plan,
} from "./utils";
import OrderSummary from "./components/OrderSummary";
import { useMemo, useState } from "react";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import { useCreateContract, useUpdateContract } from "@apis/billing";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { useUserAnalytics } from "../../../../hooks/useAnalytics";
import { EventName, EventType, ViewTypeEnum, ViewType } from "@utils/analytics";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { getDaysLeft } from "@scenes/more/MoreScreen/utils";

const PurchaseSeats = ({
  onClose,
  setShowSuccessPopup,
  setSuccessResponseData,
}: {
  onClose: () => void;
  setShowSuccessPopup: (show: boolean) => void;
  setSuccessResponseData: (data: any) => void;
}) => {
  const userInfo = useUserInfo();
  const { typography, theme, t } = useScreenOptionsHooks();
  const currentPlan =
    userInfo?.user?.plan_details?.plan.is_trial && !userInfo.isEnrolledTrial()
      ? userInfo?.user?.plan_details?.upcoming_subscription
      : userInfo?.user?.plan_details?.plan;
  const updateContract =
    userInfo.user.feature_settings?.display_saas_plan &&
    currentPlan?.purchased_seats === 0
      ? useCreateContract()
      : useUpdateContract();
  const globalData = useGlobalData();
  const plansData = globalData?.subscription_config;
  const selectedPlan =
    plansData?.plans.filter((plan: Plan) =>
      userInfo?.user?.plan_details?.zenskar_pricings?.some(
        (p) => p.product_id === plan.plan_id
      )
    )[0] || plansData?.plans[0];
  const is_saas_plan =
    currentPlan?.plan_type === "saas_plan"
      ? currentPlan?.purchased_seats === 0
      : userInfo.isEnrolledTrial();
  const is_paid_saas_plan =
    (currentPlan?.plan_type === "saas_plan" || userInfo.isEnrolledTrial()) &&
    currentPlan?.purchased_seats > 0;
  const showPlanDetails = is_saas_plan ? false : true;
  const { trackButtonEvent } = useUserAnalytics();
  const base_paid_users = currentPlan?.paid_users || 0;
  const paid_users =
    base_paid_users > 0
      ? base_paid_users - selectedPlan?.base_included_users
      : 0;
  const [additionalUsers, setAdditionalUsers] = useState<number>(0);
  const totals = computePurchaseSeatsPrice(
    selectedPlan,
    additionalUsers,
    userInfo
  );
  const { expiryDate } = getDaysLeft(currentPlan?.expiry ?? "") as {
    expiryDate: string;
  };
  const dummyData = {
    plan_name: {
      id: "plan_name",
      label: t("managePlan.popUp.plan.text", "Plan"),
      value: selectedPlan?.plan_name,
    },
    ...(!is_paid_saas_plan && {
      plan_base_users: {
        id: "plan_base_users",
        label: t("managePlan.popUp.baseUser.text", "Base User(s)"),
        value: selectedPlan?.base_included_users ?? 0,
      },
    }),
    ...(paid_users > 0 &&
      !is_paid_saas_plan && {
        plan_purchased_users: {
          id: "plan_purchased_users",
          label: t(
            "managePlan.popUp.purchasedUsers.text",
            "Purchased Additional User(s)"
          ),
          value: paid_users,
        },
      }),
    expiry: {
      id: "expiry",
      label: t("managePlan.popUp.renewal.text", "Renewal"),
      value: userInfo?.user?.plan_details?.plan.is_trial
        ? formatDate(
            userInfo?.user?.plan_details?.upcoming_subscription?.end_date
          )
        : expiryDate ?? "",
    },
  };

  const lines = useMemo(() => {
    const feeLabel = `${t(
      "managePlan.popUp.creditCardFee.text",
      "Processing Fee"
    )} (${selectedPlan?.credit_card_fee_percent ?? 0}%)`;
    const taxLabel = `${t("managePlan.popUp.taxes.text", "Taxes")} (${
      selectedPlan?.tax_percent ?? 0
    }%)`;
    return [
      {
        label: `${t(
          "managePlan.popUp.additionalUsers.text",
          "Additional Users"
        )} (${additionalUsers})`,
        value: totals.additionalUsersPrice,
      },
      ...(userInfo?.user?.is_tax_applicable
        ? [
            { label: taxLabel, value: totals.tax },
            {
              label: t("managePlan.popUp.subtotal.text", "Subtotal"),
              value: totals.taxedTotal,
            },
          ]
        : []),
      { label: feeLabel, value: totals.fee },
    ];
  }, [
    selectedPlan,
    totals,
    additionalUsers,
    userInfo?.user?.is_tax_applicable,
  ]);

  const pricings = (
    type?: "purchase_seats" | "update_plan",
    additional_user: number = 0
  ) => {
    const basePricings = userInfo?.user?.plan_details?.zenskar_pricings || [];

    if (type !== "purchase_seats") return basePricings;

    const today = formatToday();
    let updated = false;

    const result = basePricings.map((pricing) => {
      if (!pricing?.pricing_quantity) return pricing;

      const quantities = Array.isArray(pricing.pricing_quantity)
        ? [...pricing.pricing_quantity]
        : [];
      const parseDate = (str) => {
        const [day, mon, year] = str.split("-");
        return new Date(`${day} ${mon} ${year}`);
      };
      const matchingQuantity = quantities.find(
        (q) => parseDate(q.date) >= parseDate(today)
      );
      const index = quantities.findIndex(
        (q) => parseDate(q.date) >= parseDate(today)
      );
      if (matchingQuantity) {
        quantities[index] = {
          ...quantities[index],
          user_count: quantities[index].user_count + additional_user,
        };
        updated = true;
      } else {
        quantities.push({ date: today, user_count: additional_user });
        updated = true;
      }

      return {
        ...pricing,
        pricing_quantity: quantities,
      };
    });

    // If NO pricing had pricing_quantity, add a new one
    if (!updated) {
      result.push({
        product_id: selectedPlan?.additional_user_plan.id,
        pricing_id: selectedPlan?.additional_user_plan.pricing_id,
        pricing_quantity: [{ date: today, user_count: additional_user }],
      });
    }

    return result;
  };

  const handleCreateContract = () => {
    trackButtonEvent(
      EventName.BUTTON_CLICK,
      EventType.PURCHASE_SEATS_CONFIRM_BUTTON_CLICK,
      { component_type: ViewType.POP_UP },
      ViewTypeEnum?.PURCHASE_SEATS_POP_UP
    );
    updateContract.mutate(
      {
        plan_type: selectedPlan?.plan_type,
        pricings: pricings("purchase_seats", additionalUsers),
      },
      {
        onSuccess: () => {
          setSuccessResponseData({
            message: t(
              "purchaseSeats.popUp.successPopUp.description.text",
              `<div>We've sent the <strong style="font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 800; line-height: 20px;">invoice and payment</strong> link to your email.
                It may take a few minutes to arrive.</div>
                <div style="height: 12px;"></div>
                <div>If you don't receive it within 10 minutes, please contact <a href="mailto:billing@vdbapp.com" target="_blank" style="color: #007AFF; text-decoration: none;">billing@vdbapp.com</a> for assistance.</div>`,
              { seats: additionalUsers }
            ),
          });
          setShowSuccessPopup(true);
          onClose();
        },
        onError: (error: any) => {
          showToast(
            undefined,
            error.data.message,
            ToastTypes.ERROR,
            true,
            true
          );
        },
      }
    );
  };

  return (
    <Overlay>
      {showPlanDetails && (
        <>
          <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
            {t("purchaseSeats.popUp.plan.text", "Current Plan")}
          </VDBText>
          <Spacer size={SpaceType.Basic} />
          {Object.values(dummyData).map((value, index) => (
            <>
              <InfoContainer>
                <KeyContainer>
                  <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
                    {value.label}
                  </VDBText>
                </KeyContainer>
                <ValueContainer>
                  <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
                    {value.value}
                  </VDBText>
                </ValueContainer>
              </InfoContainer>
              {index < Object.values(dummyData).length - 1 && (
                <Spacer size={SpaceType.Basic} />
              )}
            </>
          ))}
          <Spacer size={SpaceType.Big} />
        </>
      )}
      {showPlanDetails && (
        <>
          <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
            {t(
              "managePlan.popUp.additionalUsers.text",
              "Purchase Additional Users"
            )}
          </VDBText>
          <Spacer size={SpaceType.Basic} />
        </>
      )}
      <UserSelector
        id={selectedPlan?.plan_id ?? 0}
        baseIncludedUsers={selectedPlan?.base_included_users ?? 0}
        pricePerAdditionalUser={totals.additionalUsersPrice ?? 0}
        value={additionalUsers}
        onChange={(value) => setAdditionalUsers(value)}
        item={selectedPlan}
        type="purchase_seats"
        is_saas_plan={is_saas_plan}
        fromPurchaseSeats={true}
      />
      <Spacer size={SpaceType.Big} />
      <OrderSummary lines={lines} estimatedTotal={totals.estimatedTotal} />
      <Spacer size={SpaceType.Basic} />
      <WarningContainer>
        <VDBIcon
          name={VDBIcons.WARNING}
          size={24}
          color={theme["warn-01"]}
          variant={IconsVariant.VDBCustom}
        />
        <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
          {t(
            "purchaseSeats.popUp.disclaimer.text",
            "Full rate shown. Your invoice may differ because you are billed only for the remaining subscription time."
          )}
        </VDBText>
      </WarningContainer>
      <Spacer size={SpaceType.large} />
      <ButtonContainer>
        <Button
          title={t("purchaseSeats.popUp.button.cancel.text", "Cancel")}
          onPress={onClose}
          type={ButtonType.secondary}
          category={ButtonCategory.default}
          width={"48%"}
          disabled={updateContract.isPending}
        />
        <Button
          title={t("purchaseSeats.popUp.button.confirm.text", "Continue")}
          onPress={handleCreateContract}
          type={ButtonType.primary}
          category={ButtonCategory.default}
          width={"48%"}
          disabled={additionalUsers === 0 || updateContract.isPending}
          loading={updateContract.isPending}
        />
      </ButtonContainer>
    </Overlay>
  );
};

const PurchaseSeatsPopup = ({
  isOpen,
  onClose,
  setShowSuccessPopup,
  setSuccessResponseData,
}: {
  isOpen: boolean;
  onClose: () => void;
  setShowSuccessPopup: (show: boolean) => void;
  setSuccessResponseData: (data: any) => void;
}) => {
  const { t } = useScreenOptionsHooks();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      paddingLeft={0}
      paddingRight={0}
      paddingBottom={0}
      paddingTop={0}
      noPadding
      headerTitle={t("purchaseSeats.popUp.title.text", "Purchase Seats")}
    >
      <PurchaseSeats
        onClose={onClose}
        setShowSuccessPopup={setShowSuccessPopup}
        setSuccessResponseData={setSuccessResponseData}
      />
    </Modal>
  );
};

const Overlay = styled.View`
  padding-horizontal: ${SpaceType.Double}px;
  padding-top: ${SpaceType.Double}px;
  padding-bottom: ${SpaceType.ExtraLarge}px;
`;

const InfoContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;

const KeyContainer = styled.View`
  width: 50%;
`;

const ValueContainer = styled.View``;

const ButtonContainer = styled.View`
  flex-direction: row;
  justify-content: center;
  gap: ${SpaceType.Double}px;
`;

const WarningContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border-radius: ${SpaceType.Basic}px;
  background-color: ${({ theme }: { theme: any }) => theme["warn-02"]};
  padding: ${SpaceType.Basic}px;
  gap: ${SpaceType.Basic}px;
`;

export default PurchaseSeatsPopup;
